import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'

interface NoiseBackgroundProps {
  /** Base frequency for noise (lower = larger grain) */
  baseFrequency?: number
  /** Number of octaves for noise complexity */
  numOctaves?: number
  /** Opacity of the noise overlay (0-1) */
  opacity?: number
  /** Whether to animate the noise */
  animate?: boolean
  /** Animation speed in seconds per cycle */
  animationSpeed?: number
  /** Position mode: 'fixed' for viewport-fixed, 'absolute' for parent-relative (parallax) */
  position?: 'fixed' | 'absolute'
}

export function NoiseBackground({
  baseFrequency = 0.65,
  numOctaves = 4,
  opacity = 0.4,
  animate = true,
  animationSpeed = 8,
  position = 'fixed',
}: NoiseBackgroundProps) {
  const turbulenceRef = useRef<SVGFETurbulenceElement>(null)

  useEffect(() => {
    if (!animate || !turbulenceRef.current) return

    // Animate the seed value to create a subtle shimmer effect
    const tl = gsap.timeline({ repeat: -1 })

    tl.to(turbulenceRef.current, {
      attr: { seed: 100 },
      duration: animationSpeed,
      ease: 'none',
    })

    return () => {
      tl.kill()
    }
  }, [animate, animationSpeed])

  return (
    <>
      {/* SVG Filter Definition */}
      <svg className="absolute w-0 h-0" aria-hidden="true">
        <defs>
          <filter id="noise-filter" x="0%" y="0%" width="100%" height="100%">
            <feTurbulence
              ref={turbulenceRef}
              type="fractalNoise"
              baseFrequency={baseFrequency}
              numOctaves={numOctaves}
              seed={0}
              stitchTiles="stitch"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="desaturatedNoise"
            />
            <feComponentTransfer in="desaturatedNoise" result="adjustedNoise">
              <feFuncA type="linear" slope={opacity} intercept="0" />
            </feComponentTransfer>
            <feBlend in="SourceGraphic" in2="adjustedNoise" mode="multiply" />
          </filter>
        </defs>
      </svg>

      {/* Background Layer */}
      <div
        className={`${position} inset-0 -z-10 pointer-events-none`}
        aria-hidden="true"
      >
        {/* Base gradient background */}
        <div className="absolute inset-0 bg-brand-primary-deep" />

        {/* Gradient overlay from top */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse 80% 50% at 50% 0%, rgb(var(--brand-primary)) 0%, transparent 60%)`,
          }}
        />

        {/* Noise overlay - black noise for darkening effect */}
        <div
          className="absolute inset-0"
          style={{
            filter: 'url(#noise-filter)',
            backgroundColor: 'transparent',
          }}
        >
          {/* This div receives the noise filter */}
          <div className="w-full h-full" />
        </div>

        {/* Subtle vignette effect */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at center, transparent 0%, rgb(var(--bg-base) / 0.3) 100%)`,
          }}
        />
      </div>
    </>
  )
}
